import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRightOutlined, SearchOutlined } from '@ant-design/icons';
import { Drawer, Input, List, Space, Tag, Typography } from 'antd';

const { Text, Paragraph } = Typography;

// "How do I..." / "Where can I..." entries — sourced from the sitemap
// (klickly/repos/admin-brands-front-sitemap.md). Every entry points at a real
// route in this app. Add a new topic as features land; the search + category
// filtering scale up to a few hundred entries before we'd need fuse.js.
//
// Fields: q = question, a = one-line answer, path = where to go, tag = section
// header, keywords = extra search terms (synonyms the phrasing might miss).
const TOPICS = [
    // Product review
    {
        q: 'Where do I approve or reject products?',
        a: 'Brands → Products → In review. Click a row to open the product; use the decision rail on the right, or keyboard A / R.',
        path: '/brands-v2/review/products/in-review',
        tag: 'Product review',
        keywords: 'approve reject moderate'
    },
    {
        q: 'How do I approve or reject products in bulk?',
        a: 'Open the Products In-review queue, select multiple rows with the checkbox, then use the floating action bar at the bottom. A 5-second undo toast appears after each bulk action.',
        path: '/brands-v2/review/products/in-review',
        tag: 'Product review',
        keywords: 'bulk multi select mass'
    },
    {
        q: 'Where do I see already-rejected products?',
        a: 'Brands → Products → Rejected. Includes the reason and timestamp.',
        path: '/brands-v2/review/products/rejected',
        tag: 'Product review'
    },
    {
        q: 'Where do I see accepted products?',
        a: 'Brands → Products → Accepted.',
        path: '/brands-v2/review/products/accepted',
        tag: 'Product review'
    },

    // Custom creatives
    {
        q: 'Where do I review custom creatives?',
        a: 'Brands → Custom Creatives. Drill from accounts → campaigns → unit grid; decisions ship in one batch per campaign.',
        path: '/brands-v2/custom-creatives/in-review',
        tag: 'Custom creatives'
    },
    {
        q: 'Where are the AWA creative units for a brand?',
        a: 'Open the account → AWA · Creative units tab. Configure sizes/layouts there.',
        path: '/brands-v2/accounts',
        tag: 'Custom creatives',
        keywords: 'awa widget appearance'
    },

    // Accounts
    {
        q: 'Where do I find a brand / account?',
        a: 'Brands → Accounts. Or press Cmd/Ctrl+K and start typing the store name.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'brand store find search'
    },
    {
        q: 'Where can I see a brand settings (commissions, attribution, payments)?',
        a: 'Open the account → Settings tab. The left nav groups the 9 sub-forms into General / Financial / Ad platform / Integrations.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'commissions attribution payment cpm google ads feature flags'
    },
    {
        q: 'Where do I see users on a brand account?',
        a: 'Open the account → Users tab. Shows everyone with access, their role, last login, and a Login button to impersonate.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'members access users team'
    },
    {
        q: 'How do I log in as a brand user?',
        a: 'Account → Users tab → click the Login button on a row. Opens the brand dashboard in a new tab as that user.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'impersonate login switch user'
    },
    {
        q: 'How do I invite a user to a brand?',
        a: 'Invites are created from the brand dashboard, not the admin. Account → Users tab → "Invite from brand dashboard" button opens the right page in a new tab.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'invite new user add team member'
    },
    {
        q: 'Where do I manage restrictions (blocked emails / domains / IPs)?',
        a: 'Account → Restrictions tab. Three tables: domain, email, IP. Add or bulk-remove.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'block ban email domain ip restriction fraud'
    },
    {
        q: 'Where are blocked products for a brand?',
        a: 'Account → Blocked products tab. Two sub-tabs: commission-blocked vs widget-blocked.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'blocked product hide widget commission'
    },
    {
        q: 'Where do I see a brand campaigns?',
        a: 'Account → Campaigns tab. Search + status filter; each row has an Open button that deep-links to the brand dashboard campaign editor.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'campaign promotion priority'
    },
    {
        q: 'Where is the DSP (Basis) creative list for a brand?',
        a: 'Account → DSP creatives tab. Shows each creative and its review status; a legend explains each status.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'dsp basis creative status'
    },
    {
        q: 'Where are script tags / pixel install status?',
        a: 'Account → Script tags tab. Consolidated "Health" column per domain — click the chip for the per-check breakdown.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'pixel script tag install health domain'
    },
    {
        q: 'How do I configure Google Analytics 4 / TripleWhale for a brand?',
        a: 'Account → Analytics integrations tab. Sub-tabs for General / GA4 / TripleWhale.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'ga4 google analytics triple whale tracking'
    },
    {
        q: 'How do I disconnect a store or clean up performance data?',
        a: 'On the account header, use the "…" menu next to Back / Open store. Dev-env only. Both actions have a confirm modal.',
        path: '/brands-v2/accounts',
        tag: 'Accounts',
        keywords: 'disconnect delete shutdown cleanup performance'
    },

    // Orders
    {
        q: 'Where do I find an order?',
        a: 'Brands → Orders. Search by Klickly order number / email / phone. Filter by brand or date range.',
        path: '/brands-v2/orders',
        tag: 'Orders',
        keywords: 'find search order number email phone'
    },
    {
        q: 'Where do I see order details, refunds, attribution?',
        a: 'Orders page → click a row. Details drawer has refund actions; the Insights drawer shows the attribution path and analytics history.',
        path: '/brands-v2/orders',
        tag: 'Orders',
        keywords: 'order detail refund attribution insights analytics'
    },

    // Payments (brand-level)
    {
        q: 'Where do I see a brand payouts?',
        a: 'Account → Payouts tab. Filter by status and order/payout number. Status tags are clickable on OPEN / FAILED rows to process or retry.',
        path: '/brands-v2/accounts',
        tag: 'Payments',
        keywords: 'payout payment transfer bank disbursement'
    },
    {
        q: 'Where are invoices for a brand?',
        a: 'Account → Invoices tab. Includes split + refund modals for finance ops.',
        path: '/brands-v2/accounts',
        tag: 'Payments',
        keywords: 'invoice billing charge stripe'
    },
    {
        q: 'How do I change a brand payment method or billing cadence?',
        a: 'Account → Settings → Payment details (grouped under Financial in the left nav).',
        path: '/brands-v2/accounts',
        tag: 'Payments',
        keywords: 'payment method billing retry cadence schedule'
    },

    // Reports
    {
        q: 'How do I schedule a report for a brand?',
        a: 'Account → Reports tab → Create report. Pick fields, period, destination (email or Google Sheet). Next-send time is shown on each card.',
        path: '/brands-v2/accounts',
        tag: 'Reports',
        keywords: 'scheduled report email google sheet sync performance'
    },

    // Creative settings (global)
    {
        q: 'Where are global creative settings?',
        a: 'Brands → Creative settings. Upload new creatives, tune metadata, or promote drafts.',
        path: '/brands-v2/creative-settings',
        tag: 'Creative settings'
    },

    // Privacy
    {
        q: 'Where do I handle a CCPA / data privacy request?',
        a: 'Brands → Customer privacy. Create a request, pick View or Erase, optionally send a confirmation email.',
        path: '/brands-v2/privacy',
        tag: 'Privacy',
        keywords: 'ccpa gdpr privacy data delete erase customer request'
    },

    // Uplift studies
    {
        q: 'Where are uplift / incrementality studies?',
        a: 'Brands → Uplift Studies. Create / edit / view running experiments per brand.',
        path: '/brands-v2/uplift-studies',
        tag: 'Uplift studies',
        keywords: 'uplift incremental study experiment a/b test'
    },

    // Brief
    {
        q: 'Where do I run pre-live health checks on a brand?',
        a: 'Brands → Brief → Pre-Live tab. Enter a URL, run the check, optionally send a report to Slack.',
        path: '/brands-v2/brief',
        tag: 'Brief',
        keywords: 'pre-live health check brief onboarding qa'
    },
    {
        q: 'Where do I run Day 1 / Day 14 live checks?',
        a: 'Brands → Brief → select an account → Live tab. Pick which checks to run; the modal asks for expected GMV / traffic.',
        path: '/brands-v2/brief',
        tag: 'Brief',
        keywords: 'day 1 day 14 live check gmv traffic brief'
    },

    // KCP
    {
        q: 'Where do I manage KCP accounts, publishers, rewards, upsells?',
        a: 'KCP (top nav). Each area is a sidebar entry — Accounts, Publishers, Rewards, Upsells, Advertise, Giftly.',
        path: '/kcp',
        tag: 'KCP',
        keywords: 'kcp klickly consumer platform publisher reward upsell giftly advertise'
    },
    {
        q: 'Where is Giftly?',
        a: 'KCP → Giftly. Five sub-pages: Customers, GS Products, GS Explorer, Invite codes, Tasks. Public surface at giftly.klickly.com.',
        path: '/kcp/giftly/customers',
        tag: 'KCP',
        keywords: 'giftly gift marketplace'
    },

    // System
    {
        q: 'Where do I manage admin users (add / remove, change access)?',
        a: 'System → Admin users. Each admin has an access-key array that gates menu items and routes.',
        path: '/system/admin-users',
        tag: 'System',
        keywords: 'admin user access role permission system'
    },
    {
        q: 'Where do I trigger a cache sync?',
        a: 'System → Sync → Cache sync tab. The Sync page hosts every global sync job under tabs.',
        path: '/system/sync',
        tag: 'System',
        keywords: 'cache sync invalidate refresh'
    },
    {
        q: 'Where do I see or trigger cron jobs?',
        a: 'System → Cron Jobs.',
        path: '/system/cron-jobs',
        tag: 'System',
        keywords: 'cron job scheduled task'
    },
    {
        q: 'Where are global DSP settings (not per-account)?',
        a: 'System → DSP Settings. Per-account overrides live on the Account Settings tab.',
        path: '/system/dsp/settings',
        tag: 'System',
        keywords: 'dsp basis settings global cpm'
    },
    {
        q: 'Where do I manage script tags globally or web pixels?',
        a: 'System → Sync. Script tags, Web pixels, Setup webhooks, Scope sync, Cache sync, and ES migrations all live as tabs on the Sync page. Per-brand script-tag health stays on the Account Script tags tab.',
        path: '/system/sync',
        tag: 'System',
        keywords: 'script tag pixel web setup webhooks scope es migrations elastic'
    },
    {
        q: 'Where is the Priority Calculator (explain priority for a campaign)?',
        a: 'System → Priority Calculator. Campaign rows on the Account Campaigns tab also deep-link into the calculator for that campaign.',
        path: '/system/priority-calculator',
        tag: 'System',
        keywords: 'priority calculator explain campaign math'
    },

    // Notifications
    {
        q: 'Where do I create / launch email campaigns?',
        a: 'Notifications → Campaigns. Each campaign picks a mailing list + template.',
        path: '/notifications/campaigns',
        tag: 'Notifications',
        keywords: 'email campaign send blast newsletter'
    },
    {
        q: 'Where are mailing lists (build an audience)?',
        a: 'Notifications → Mailing lists. Upload a CSV or build by condition groups.',
        path: '/notifications/mailing-lists',
        tag: 'Notifications',
        keywords: 'mailing list audience segment condition upload csv'
    },
    {
        q: 'Where are email templates?',
        a: 'Notifications → Templates. HTML editor with test-send.',
        path: '/notifications/templates',
        tag: 'Notifications',
        keywords: 'email template html editor design'
    },
    {
        q: 'Where do I manage transactional / service emails?',
        a: 'Notifications → Service mails. Rule-based automations live under Automations.',
        path: '/notifications/service-mails',
        tag: 'Notifications',
        keywords: 'service mail transactional automation trigger'
    },
    {
        q: 'Where is the email ban list?',
        a: 'Notifications → Ban list. Addresses on the ban list stop receiving our mail.',
        path: '/notifications/ban-list',
        tag: 'Notifications',
        keywords: 'ban block email unsubscribe suppress'
    },

    // Meta
    {
        q: 'How do I jump anywhere fast (keyboard)?',
        a: 'Press Cmd/Ctrl+K for the command palette (account search + page jump). Press ? for the keyboard-shortcut cheat sheet. Press g a / g o / g b etc. to navigate.',
        path: null,
        tag: 'Meta',
        keywords: 'shortcut hotkey palette keyboard quick'
    },
    {
        q: 'What do acronyms like KCP, DSP, AWA, Giftly mean?',
        a: 'Open the Glossary (book icon in the header, next to this one). 16 entries covering every internal acronym the admin uses.',
        path: null,
        tag: 'Meta',
        keywords: 'glossary acronym what is meaning define expansion'
    }
];

const normalize = (s) => (s || '').toLowerCase();

const matchesQuery = (topic, query) => {
    if (!query) return true;
    const hay = normalize(`${topic.q} ${topic.a} ${topic.tag} ${topic.keywords || ''}`);
    return query
        .split(/\s+/)
        .filter(Boolean)
        .every((t) => hay.includes(t));
};

export const HelpCenter = ({ open, onClose }) => {
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    const filtered = useMemo(() => TOPICS.filter((t) => matchesQuery(t, normalize(query.trim()))), [query]);

    // Group by tag for browsability when no query is entered; when the user
    // types, the tag still shows on each row so the source is still visible.
    const grouped = useMemo(() => {
        const order = [
            'Product review',
            'Custom creatives',
            'Accounts',
            'Orders',
            'Payments',
            'Reports',
            'Creative settings',
            'Privacy',
            'Uplift studies',
            'Brief',
            'KCP',
            'System',
            'Notifications',
            'Meta'
        ];
        const map = new Map();
        filtered.forEach((t) => {
            if (!map.has(t.tag)) map.set(t.tag, []);
            map.get(t.tag).push(t);
        });
        return order.filter((g) => map.has(g)).map((g) => ({ tag: g, items: map.get(g) }));
    }, [filtered]);

    const go = (topic) => {
        if (topic.path) {
            navigate(topic.path);
            onClose();
        }
    };

    return (
        <Drawer
            title='How do I…?'
            open={open}
            onClose={onClose}
            width={560}
            destroyOnHidden
            extra={
                <Text type='secondary' style={{ fontSize: 12 }}>
                    {filtered.length} / {TOPICS.length}
                </Text>
            }
        >
            <Paragraph type='secondary' style={{ marginBottom: 16 }}>
                Jump to the right page by what you want to do. Matching is on the question, answer, section, and
                synonyms — type &quot;refund&quot;, &quot;commission&quot;, or &quot;script tags&quot; and the relevant
                entries surface.
            </Paragraph>
            <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Type a question or keyword…'
                prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
                allowClear
                size='large'
                style={{ marginBottom: 16 }}
            />
            {grouped.length === 0 ? (
                <Text type='secondary'>No entries match your search.</Text>
            ) : (
                grouped.map(({ tag, items }) => (
                    <div key={tag} style={{ marginBottom: 20 }}>
                        <Text type='secondary' style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            {tag}
                        </Text>
                        <List
                            dataSource={items}
                            renderItem={(topic) => (
                                <List.Item
                                    key={topic.q}
                                    onClick={() => go(topic)}
                                    style={{
                                        alignItems: 'flex-start',
                                        cursor: topic.path ? 'pointer' : 'default',
                                        padding: '10px 0'
                                    }}
                                >
                                    <Space direction='vertical' size={4} style={{ width: '100%' }}>
                                        <Space size={8} style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Text strong style={{ fontSize: 13 }}>
                                                {topic.q}
                                            </Text>
                                            {topic.path ? (
                                                <Tag
                                                    icon={<ArrowRightOutlined />}
                                                    color='blue'
                                                    style={{ margin: 0, fontSize: 11 }}
                                                >
                                                    Open
                                                </Tag>
                                            ) : null}
                                        </Space>
                                        <Text type='secondary' style={{ fontSize: 12 }}>
                                            {topic.a}
                                        </Text>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    </div>
                ))
            )}
        </Drawer>
    );
};
